import React, { useMemo } from 'react';
import { Anchor, capitalizeFirstLetter, ListItemType } from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { ROUTES } from '@/common';
import { DEFAULT_DATE_FORMAT, EMPTY_FIELD_VALUE } from '@/common/constants';
import {
  CheckCardsListItem,
  CheckMatchingType,
  LockboxCheck,
  LockboxCheckType,
} from '@/modules/employer/components/Lockbox/lockbox.types';
import { formatCurrency } from '@/utils';

interface Props {
  checks?: LockboxCheck[];
  ocrCheckNumber?: string;
  ocrAmount?: number;
}

const formatDate = (date?: string) =>
  (date ? dayjs(date).format(DEFAULT_DATE_FORMAT) : EMPTY_FIELD_VALUE);

const formatAddress = (address?: LockboxCheck['address']) =>
  (address
    ? [address.line1, address.line2, address.city, address.state?.code, address.zipcode]
      .map((part) => part?.trim())
      .filter(Boolean)
      .join(', ')
    : '') || EMPTY_FIELD_VALUE;

export const getCheckMatchingType = (
  ocrCheckNumber?: string,
  ocrAmount?: number,
  check?: LockboxCheck,
): CheckMatchingType | undefined => {
  const checkId = check?.checkId;

  if (ocrCheckNumber === undefined || checkId === undefined || `${ocrCheckNumber}` !== `${checkId}`) {
    return undefined;
  }

  if (typeof ocrAmount === 'number' && typeof check?.amount === 'number' && ocrAmount === check.amount) {
    return CheckMatchingType.FULL_MATCH;
  }

  return CheckMatchingType.AMOUNT_NOT_MATCHED;
};

const useCheckCardsList = ({ checks, ocrCheckNumber, ocrAmount }: Props) => {
  const fields: CheckCardsListItem[] = useMemo(() => {
    if (!checks?.length) {
      return [];
    }

    return checks.map((check) => {
      const isLockboxCheck = check?.checkType === LockboxCheckType.LOCKBOX || !!check?.lockboxId;

      const checkNumberField = {
        key: 'checkNumber',
        type: ListItemType.Text,
        title: 'Check #',
        value: check?.checkId || EMPTY_FIELD_VALUE,
      };

      const commonTailFields = [
        {
          key: 'issuedDate',
          type: ListItemType.Text,
          title: 'Issued date',
          value: formatDate(check?.issuedDate),
        },
        {
          key: 'statusDate',
          type: ListItemType.Text,
          title: 'Status date',
          value: formatDate(check?.statusDate),
        },
        {
          key: 'status',
          type: ListItemType.Text,
          title: 'Status',
          value: check?.status ? capitalizeFirstLetter(check.status.toLowerCase().replace(/_/g, ' ')) : EMPTY_FIELD_VALUE,
        },
        {
          key: 'checkSentTo',
          type: ListItemType.Text,
          title: 'Check Sent to',
          value: formatAddress(check?.address),
        },
        {
          key: 'payTo',
          type: ListItemType.Text,
          title: 'Pay To',
          value: check?.payTo || EMPTY_FIELD_VALUE,
        },
      ];

      const headFields = isLockboxCheck
        ? [
          checkNumberField,
          {
            key: 'lockboxId',
            type: ListItemType.Text,
            title: 'Lockbox ID',
            ...check?.lockboxId
              ? {
                node: (
                  <Anchor href={ROUTES.LOCKBOX_DETAILS(check.lockboxId)} target="_blank" weight={400}>
                    {check.lockboxId}
                  </Anchor>
                ),
              }
              : { value: EMPTY_FIELD_VALUE },
          },
          {
            key: 'amount',
            type: ListItemType.Text,
            title: 'Amount',
            value: typeof check?.amount === 'number' ? formatCurrency(check.amount) || EMPTY_FIELD_VALUE : EMPTY_FIELD_VALUE,
          },
        ]
        : [
          checkNumberField,
          {
            key: 'employee',
            type: ListItemType.Text,
            title: 'Employee',
            value: check?.employeeName || EMPTY_FIELD_VALUE,
          },
          {
            key: 'relatedClaims',
            type: ListItemType.Text,
            title: 'Related claims',
            style: {
              whiteSpace: 'normal',
              overflow: 'visible',
              textOverflow: 'clip',
            },
            ...check?.claimIds?.length
              ? {
                node: (
                  <>
                    {check.claimIds.map((claimId, index) => (
                      <React.Fragment key={claimId}>
                        {index > 0 ? ', ' : ''}
                        <Anchor href={ROUTES.CLAIMS_PROCESSOR(claimId)} target="_blank" weight={400}>
                          {claimId}
                        </Anchor>
                      </React.Fragment>
                    ))}
                  </>
                ),
              }
              : { value: EMPTY_FIELD_VALUE },
          },
        ];

      const matchingType = getCheckMatchingType(ocrCheckNumber, ocrAmount, check);

      return {
        id: `${check?.checkId}`,
        warnings: matchingType ? [matchingType] : undefined,
        fields: [...headFields, ...commonTailFields],
      };
    });
  }, [checks, ocrCheckNumber, ocrAmount]);

  return {
    fields,
  };
};

export default useCheckCardsList;
